import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Dimensions } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ProgressBar,
  List,
  Avatar,
  Badge,
  Divider,
  IconButton,
  Portal,
  Dialog,
  TextInput,
  Switch,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  Users,
  Target,
  Clock,
  BookOpen,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  BarChart3,
  Edit,
  Plus,
  Trash2,
  RefreshCw,
  Lightbulb,
  AlertCircle,
  Star,
  Calendar,
  Timer,
  Coffee,
  Filter,
  Search,
  User,
  Settings,
  Palette,
  Music,
  Video,
  Heart,
  Eye,
  ThumbsUp,
  MessageCircle,
} from 'lucide-react-native';

interface UserPreference {
  id: string;
  category:
    | 'content_type'
    | 'learning_style'
    | 'difficulty'
    | 'time_of_day'
    | 'equipment'
    | 'coffee_type';
  value: string;
  confidence: number;
  lastUpdated: string;
  source: 'explicit' | 'inferred' | 'behavioral';
  impact: 'high' | 'medium' | 'low';
}

interface ContentRecommendation {
  id: string;
  title: string;
  type: 'video' | 'reading' | 'practice' | 'interactive' | 'audio';
  category: string;
  difficulty: number;
  estimatedTime: number;
  personalizationScore: number;
  reasoning: string[];
  tags: string[];
  userRating?: number;
  isRecommended: boolean;
}

interface BehavioralPattern {
  id: string;
  pattern: string;
  frequency: number;
  confidence: number;
  lastObserved: string;
  impact: 'positive' | 'negative' | 'neutral';
  recommendation: string;
}

interface PersonalizationInsight {
  id: string;
  type: 'preference' | 'behavior' | 'performance' | 'engagement';
  title: string;
  description: string;
  confidence: number;
  action: string;
  impact: 'high' | 'medium' | 'low';
}

const { width } = Dimensions.get('window');

export function AIPersonalizationScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const [selectedContent, setSelectedContent] =
    useState<ContentRecommendation | null>(null);
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);
  const [showInsightsDialog, setShowInsightsDialog] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Mock user preferences data
  const [userPreferences, setUserPreferences] = useState<UserPreference[]>([
    {
      id: 'pref-1',
      category: 'content_type',
      value: 'video',
      confidence: 0.94,
      lastUpdated: '2024-01-15',
      source: 'behavioral',
      impact: 'high',
    },
    {
      id: 'pref-2',
      category: 'learning_style',
      value: 'visual',
      confidence: 0.87,
      lastUpdated: '2024-01-14',
      source: 'explicit',
      impact: 'high',
    },
    {
      id: 'pref-3',
      category: 'difficulty',
      value: 'intermediate',
      confidence: 0.82,
      lastUpdated: '2024-01-13',
      source: 'inferred',
      impact: 'medium',
    },
    {
      id: 'pref-4',
      category: 'time_of_day',
      value: 'morning',
      confidence: 0.78,
      lastUpdated: '2024-01-12',
      source: 'behavioral',
      impact: 'medium',
    },
    {
      id: 'pref-5',
      category: 'equipment',
      value: 'pour_over',
      confidence: 0.91,
      lastUpdated: '2024-01-11',
      source: 'behavioral',
      impact: 'high',
    },
    {
      id: 'pref-6',
      category: 'coffee_type',
      value: 'light_roast',
      confidence: 0.85,
      lastUpdated: '2024-01-10',
      source: 'explicit',
      impact: 'medium',
    },
  ]);

  const [contentRecommendations, setContentRecommendations] = useState<
    ContentRecommendation[]
  >([
    {
      id: 'content-1',
      title: 'Advanced Pour-Over Techniques',
      type: 'video',
      category: 'Brewing Methods',
      difficulty: 4,
      estimatedTime: 25,
      personalizationScore: 0.96,
      reasoning: [
        'Matches your visual learning preference',
        'Aligns with your pour-over equipment focus',
        'Difficulty level matches your current progression',
        'Morning time slot preference detected',
      ],
      tags: ['pour-over', 'advanced', 'visual', 'morning'],
      isRecommended: true,
    },
    {
      id: 'content-2',
      title: 'Light Roast Coffee Origins',
      type: 'reading',
      category: 'Coffee Knowledge',
      difficulty: 3,
      estimatedTime: 20,
      personalizationScore: 0.89,
      reasoning: [
        'Matches your light roast preference',
        'Reading format for focused learning',
        'Intermediate difficulty level',
        'Knowledge gap identified in origins',
      ],
      tags: ['origins', 'light-roast', 'reading', 'intermediate'],
      isRecommended: true,
    },
    {
      id: 'content-3',
      title: 'Interactive Grind Size Workshop',
      type: 'interactive',
      category: 'Practical Skills',
      difficulty: 3,
      estimatedTime: 30,
      personalizationScore: 0.84,
      reasoning: [
        'Hands-on learning matches your style',
        'Builds on your current equipment',
        'Practical application focus',
        'Optimal time commitment',
      ],
      tags: ['grinding', 'interactive', 'practical', 'workshop'],
      isRecommended: true,
    },
    {
      id: 'content-4',
      title: 'Espresso Basics for Beginners',
      type: 'video',
      category: 'Brewing Methods',
      difficulty: 2,
      estimatedTime: 35,
      personalizationScore: 0.72,
      reasoning: [
        'Video format preference',
        'Lower difficulty for skill building',
        'Equipment expansion opportunity',
        'Morning learning preference',
      ],
      tags: ['espresso', 'beginner', 'video', 'morning'],
      isRecommended: false,
    },
  ]);

  const [behavioralPatterns, setBehavioralPatterns] = useState<
    BehavioralPattern[]
  >([
    {
      id: 'pattern-1',
      pattern: 'High engagement with video content',
      frequency: 0.87,
      confidence: 0.94,
      lastObserved: '2024-01-15',
      impact: 'positive',
      recommendation: 'Prioritize video content in recommendations',
    },
    {
      id: 'pattern-2',
      pattern: 'Morning learning sessions (7-9 AM)',
      frequency: 0.78,
      confidence: 0.89,
      lastObserved: '2024-01-15',
      impact: 'positive',
      recommendation: 'Schedule important content for morning hours',
    },
    {
      id: 'pattern-3',
      pattern: 'Pour-over equipment focus',
      frequency: 0.92,
      confidence: 0.96,
      lastObserved: '2024-01-14',
      impact: 'positive',
      recommendation: 'Continue pour-over focused content',
    },
    {
      id: 'pattern-4',
      pattern: 'Low engagement with audio content',
      frequency: 0.23,
      confidence: 0.85,
      lastObserved: '2024-01-13',
      impact: 'negative',
      recommendation: 'Reduce audio content recommendations',
    },
  ]);

  const [personalizationInsights, setPersonalizationInsights] = useState<
    PersonalizationInsight[]
  >([
    {
      id: 'insight-1',
      type: 'preference',
      title: 'Strong Visual Learning Preference',
      description:
        'Your engagement with video content is 34% higher than other formats',
      confidence: 0.94,
      action: 'Prioritize video content in your learning path',
      impact: 'high',
    },
    {
      id: 'insight-2',
      type: 'behavior',
      title: 'Morning Learning Peak',
      description: "You're most productive during morning hours (7-9 AM)",
      confidence: 0.89,
      action: 'Schedule complex topics for morning sessions',
      impact: 'high',
    },
    {
      id: 'insight-3',
      type: 'performance',
      title: 'Pour-Over Expertise Building',
      description: 'Your pour-over skills are developing faster than expected',
      confidence: 0.87,
      action: 'Consider advanced pour-over techniques',
      impact: 'medium',
    },
    {
      id: 'insight-4',
      type: 'engagement',
      title: 'Interactive Content Preference',
      description: 'You engage more deeply with hands-on activities',
      confidence: 0.82,
      action: 'Increase practical workshop content',
      impact: 'medium',
    },
  ]);

  const handleContentPress = (content: ContentRecommendation) => {
    setSelectedContent(content);
  };

  const handleUpdatePreference = async (
    preferenceId: string,
    newValue: string
  ) => {
    setIsAnalyzing(true);

    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Update preference
    setUserPreferences(prev =>
      prev.map(p =>
        p.id === preferenceId
          ? {
              ...p,
              value: newValue,
              lastUpdated: new Date().toISOString().split('T')[0],
            }
          : p
      )
    );

    // Generate new insights
    const newInsight: PersonalizationInsight = {
      id: `insight-${Date.now()}`,
      type: 'preference',
      title: 'Preference Updated',
      description: `Your ${preferenceId} preference has been updated to ${newValue}`,
      confidence: 0.95,
      action: 'Content recommendations will be updated accordingly',
      impact: 'medium',
    };

    setPersonalizationInsights(prev => [newInsight, ...prev]);
    setIsAnalyzing(false);
  };

  const getPreferenceCategoryIcon = (category: string) => {
    switch (category) {
      case 'content_type':
        return <Video size={20} color={theme.colors.primary} />;
      case 'learning_style':
        return <Brain size={20} color={theme.colors.secondary} />;
      case 'difficulty':
        return <Target size={20} color={theme.colors.tertiary} />;
      case 'time_of_day':
        return <Clock size={20} color={theme.colors.primary} />;
      case 'equipment':
        return <Coffee size={20} color={theme.colors.secondary} />;
      case 'coffee_type':
        return <Coffee size={20} color={theme.colors.tertiary} />;
      default:
        return <Settings size={20} color={theme.colors.outline} />;
    }
  };

  const getSourceColor = (source: UserPreference['source']) => {
    switch (source) {
      case 'explicit':
        return theme.colors.primary;
      case 'inferred':
        return theme.colors.secondary;
      case 'behavioral':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getPatternImpactColor = (impact: BehavioralPattern['impact']) => {
    switch (impact) {
      case 'positive':
        return theme.colors.primary;
      case 'negative':
        return theme.colors.error;
      case 'neutral':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const renderPreferenceCard = (preference: UserPreference) => (
    <Card key={preference.id} style={styles.preferenceCard}>
      <Card.Content>
        <View style={styles.preferenceHeader}>
          <View style={styles.preferenceIcon}>
            {getPreferenceCategoryIcon(preference.category)}
          </View>
          <View style={styles.preferenceInfo}>
            <Text variant='titleMedium' style={styles.preferenceTitle}>
              {preference.category
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())}
            </Text>
            <Text variant='bodyMedium' style={styles.preferenceValue}>
              {preference.value
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())}
            </Text>
          </View>
          <View style={styles.preferenceMetrics}>
            <Chip
              mode='outlined'
              textStyle={{ color: getSourceColor(preference.source) }}
              style={{ borderColor: getSourceColor(preference.source) }}
              compact
            >
              {preference.source}
            </Chip>
            <Badge style={styles.confidenceBadge}>
              {Math.round(preference.confidence * 100)}%
            </Badge>
          </View>
        </View>

        <View style={styles.preferenceDetails}>
          <View style={styles.preferenceDetail}>
            <Text variant='labelSmall'>Impact</Text>
            <Chip
              mode='outlined'
              textStyle={{ color: getImpactColor(preference.impact) }}
              style={{ borderColor: getImpactColor(preference.impact) }}
              compact
            >
              {preference.impact}
            </Chip>
          </View>
          <View style={styles.preferenceDetail}>
            <Text variant='labelSmall'>Last Updated</Text>
            <Text variant='bodySmall'>{preference.lastUpdated}</Text>
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  const renderContentCard = (content: ContentRecommendation) => (
    <Card
      key={content.id}
      style={[
        styles.contentCard,
        content.isRecommended && styles.recommendedCard,
      ]}
      onPress={() => handleContentPress(content)}
    >
      <Card.Content>
        <View style={styles.contentHeader}>
          <View style={styles.contentTitleRow}>
            <Text variant='titleMedium' style={styles.contentTitle}>
              {content.title}
            </Text>
            {content.isRecommended && (
              <Chip mode='flat' compact style={styles.recommendedChip}>
                Recommended
              </Chip>
            )}
          </View>

          <View style={styles.contentBadges}>
            <Chip mode='outlined' compact>
              {content.type}
            </Chip>
            <Chip mode='outlined' compact>
              Level {content.difficulty}
            </Chip>
            <Chip mode='outlined' compact>
              {content.estimatedTime} min
            </Chip>
          </View>
        </View>

        <View style={styles.personalizationScore}>
          <Text variant='labelSmall'>Personalization Score</Text>
          <View style={styles.scoreRow}>
            <ProgressBar
              progress={content.personalizationScore}
              color={theme.colors.primary}
              style={styles.scoreBar}
            />
            <Text variant='titleMedium' style={styles.scoreValue}>
              {Math.round(content.personalizationScore * 100)}%
            </Text>
          </View>
        </View>

        <View style={styles.reasoningSection}>
          <Text variant='labelSmall' style={styles.reasoningTitle}>
            Why This Content for You:
          </Text>
          {content.reasoning.map((reason, index) => (
            <View key={index} style={styles.reasoningItem}>
              <CheckCircle size={16} color={theme.colors.primary} />
              <Text variant='bodySmall' style={styles.reasoningText}>
                {reason}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.contentTags}>
          {content.tags.map((tag, index) => (
            <Chip key={index} mode='outlined' compact style={styles.tagChip}>
              {tag}
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderBehavioralPattern = (pattern: BehavioralPattern) => (
    <Card key={pattern.id} style={styles.patternCard}>
      <Card.Content>
        <View style={styles.patternHeader}>
          <View style={styles.patternInfo}>
            <Text variant='titleMedium' style={styles.patternTitle}>
              {pattern.pattern}
            </Text>
            <Text variant='bodySmall' style={styles.patternFrequency}>
              Frequency: {Math.round(pattern.frequency * 100)}%
            </Text>
          </View>

          <View style={styles.patternMetrics}>
            <Chip
              mode='outlined'
              textStyle={{ color: getPatternImpactColor(pattern.impact) }}
              style={{ borderColor: getPatternImpactColor(pattern.impact) }}
              compact
            >
              {pattern.impact}
            </Chip>
            <Badge style={styles.confidenceBadge}>
              {Math.round(pattern.confidence * 100)}%
            </Badge>
          </View>
        </View>

        <Text variant='bodyMedium' style={styles.patternRecommendation}>
          {pattern.recommendation}
        </Text>

        <Text variant='bodySmall' style={styles.patternLastObserved}>
          Last observed: {pattern.lastObserved}
        </Text>
      </Card.Content>
    </Card>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Info Header */}
      <Surface style={styles.header} elevation={2}>
        <View style={styles.headerContent}>
          <View style={styles.headerTitle}>
            <Users size={32} color={theme.colors.primary} />
            <View style={styles.titleContainer}>
              <Text variant='headlineMedium' style={styles.title}>
                AI Personalization
              </Text>
              <Text variant='bodyMedium' style={styles.subtitle}>
                Content and experience tailored to your preferences
              </Text>
            </View>
          </View>
        </View>
      </Surface>

      {/* Quick Stats */}
      <Surface style={styles.statsCard} elevation={1}>
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <User size={20} color={theme.colors.primary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Preferences
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {userPreferences.length}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Target size={20} color={theme.colors.secondary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              High Impact
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {userPreferences.filter(p => p.impact === 'high').length}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Brain size={20} color={theme.colors.tertiary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              AI Confidence
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {Math.round(
                (userPreferences.reduce((sum, p) => sum + p.confidence, 0) /
                  userPreferences.length) *
                  100
              )}
              %
            </Text>
          </View>
        </View>
      </Surface>

      {/* Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {/* User Preferences */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Your Preferences
            </Text>
            <Button
              mode='outlined'
              onPress={() => setShowPreferencesDialog(true)}
              compact
              icon='edit'
            >
              Edit
            </Button>
          </View>

          {userPreferences.map(renderPreferenceCard)}
        </View>

        {/* Personalized Content */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Personalized Content
            </Text>
            <Button
              mode='outlined'
              onPress={() => setShowInsightsDialog(true)}
              compact
              icon='lightbulb'
            >
              Insights
            </Button>
          </View>

          {contentRecommendations.map(renderContentCard)}
        </View>

        {/* Behavioral Patterns */}
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Learning Patterns
          </Text>

          {behavioralPatterns.map(renderBehavioralPattern)}
        </View>
      </ScrollView>

      {/* Content Details Dialog */}
      {selectedContent && (
        <Portal>
          <Dialog
            visible={!!selectedContent}
            onDismiss={() => setSelectedContent(null)}
            style={styles.dialog}
          >
            <Dialog.Title>{selectedContent.title}</Dialog.Title>

            <Dialog.Content style={styles.dialogContent}>
              <ScrollView showsVerticalScrollIndicator={false}>
                <Surface style={styles.contentDetailCard} elevation={1}>
                  <View style={styles.contentDetailHeader}>
                    <Text
                      variant='titleMedium'
                      style={styles.contentDetailTitle}
                    >
                      Personalization Details
                    </Text>
                  </View>

                  <View style={styles.contentDetailMetrics}>
                    <View style={styles.detailMetric}>
                      <Text variant='labelSmall'>Personalization Score</Text>
                      <Text variant='titleLarge' style={styles.detailValue}>
                        {Math.round(selectedContent.personalizationScore * 100)}
                        %
                      </Text>
                    </View>
                    <View style={styles.detailMetric}>
                      <Text variant='labelSmall'>Difficulty</Text>
                      <Text variant='titleMedium'>
                        Level {selectedContent.difficulty}
                      </Text>
                    </View>
                    <View style={styles.detailMetric}>
                      <Text variant='labelSmall'>Time</Text>
                      <Text variant='titleMedium'>
                        {selectedContent.estimatedTime} min
                      </Text>
                    </View>
                  </View>

                  <View style={styles.reasoningSection}>
                    <Text variant='titleMedium' style={styles.reasoningTitle}>
                      Why This Content for You:
                    </Text>
                    {selectedContent.reasoning.map((reason, index) => (
                      <View key={index} style={styles.reasoningItem}>
                        <CheckCircle size={16} color={theme.colors.primary} />
                        <Text variant='bodyMedium' style={styles.reasoningText}>
                          {reason}
                        </Text>
                      </View>
                    ))}
                  </View>
                </Surface>
              </ScrollView>
            </Dialog.Content>

            <Dialog.Actions>
              <Button onPress={() => setSelectedContent(null)}>Close</Button>
              <Button
                mode='contained'
                onPress={() => {
                  setSelectedContent(null);
                  // Start learning content
                }}
              >
                Start Learning
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
      )}

      {/* Preferences Dialog */}
      <Portal>
        <Dialog
          visible={showPreferencesDialog}
          onDismiss={() => setShowPreferencesDialog(false)}
          style={styles.dialog}
        >
          <Dialog.Title>Edit Preferences</Dialog.Title>

          <Dialog.Content style={styles.dialogContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text variant='bodyMedium' style={styles.dialogDescription}>
                Update your preferences to improve AI personalization. Our
                system will learn from your choices and adjust recommendations
                accordingly.
              </Text>

              {userPreferences.map(preference => (
                <Surface
                  key={preference.id}
                  style={styles.editPreferenceCard}
                  elevation={1}
                >
                  <View style={styles.editPreferenceHeader}>
                    <Text
                      variant='titleMedium'
                      style={styles.editPreferenceTitle}
                    >
                      {preference.category
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={styles.editPreferenceCurrent}
                    >
                      Current:{' '}
                      {preference.value
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())}
                    </Text>
                  </View>

                  <Button
                    mode='outlined'
                    onPress={() =>
                      handleUpdatePreference(preference.id, 'new_value')
                    }
                    style={styles.updatePreferenceButton}
                    loading={isAnalyzing}
                    disabled={isAnalyzing}
                  >
                    Update Preference
                  </Button>
                </Surface>
              ))}
            </ScrollView>
          </Dialog.Content>

          <Dialog.Actions>
            <Button onPress={() => setShowPreferencesDialog(false)}>
              Close
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Insights Dialog */}
      <Portal>
        <Dialog
          visible={showInsightsDialog}
          onDismiss={() => setShowInsightsDialog(false)}
          style={styles.dialog}
        >
          <Dialog.Title>AI Personalization Insights</Dialog.Title>

          <Dialog.Content style={styles.dialogContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Surface style={styles.insightsCard} elevation={1}>
                <View style={styles.insightsHeader}>
                  <Lightbulb size={24} color={theme.colors.secondary} />
                  <Text variant='titleMedium' style={styles.insightsTitle}>
                    How AI Understands You
                  </Text>
                </View>

                <Text variant='bodyMedium' style={styles.insightsDescription}>
                  These insights are generated from your learning behavior,
                  explicit preferences, and performance patterns.
                </Text>
              </Surface>

              {personalizationInsights.map(insight => (
                <Surface
                  key={insight.id}
                  style={styles.insightCard}
                  elevation={1}
                >
                  <View style={styles.insightHeader}>
                    <Chip
                      mode='outlined'
                      textStyle={{ color: getImpactColor(insight.impact) }}
                      style={{ borderColor: getImpactColor(insight.impact) }}
                      compact
                    >
                      {insight.impact} impact
                    </Chip>
                    <Badge style={styles.confidenceBadge}>
                      {Math.round(insight.confidence * 100)}%
                    </Badge>
                  </View>

                  <Text variant='titleMedium' style={styles.insightTitle}>
                    {insight.title}
                  </Text>

                  <Text variant='bodyMedium' style={styles.insightDescription}>
                    {insight.description}
                  </Text>

                  <Text variant='bodyMedium' style={styles.insightAction}>
                    <Text variant='labelMedium' style={styles.actionLabel}>
                      Action:
                    </Text>{' '}
                    {insight.action}
                  </Text>
                </Surface>
              ))}
            </ScrollView>
          </Dialog.Content>

          <Dialog.Actions>
            <Button onPress={() => setShowInsightsDialog(false)}>Close</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleContainer: {
    marginLeft: 16,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  statsCard: {
    padding: 16,
    margin: 16,
    borderRadius: 12,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    marginTop: 4,
    opacity: 0.7,
  },
  statValue: {
    marginTop: 2,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  preferenceCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  preferenceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  preferenceIcon: {
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f0f0',
  },
  preferenceInfo: {
    flex: 1,
    marginRight: 12,
  },
  preferenceTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  preferenceValue: {
    opacity: 0.7,
  },
  preferenceMetrics: {
    alignItems: 'flex-end',
  },
  confidenceBadge: {
    backgroundColor: theme => theme.colors.primary,
    marginTop: 8,
  },
  preferenceDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  preferenceDetail: {
    alignItems: 'center',
    flex: 1,
  },
  contentCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  recommendedCard: {
    borderColor: theme => theme.colors.primary,
    borderWidth: 2,
  },
  contentHeader: {
    marginBottom: 16,
  },
  contentTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  contentTitle: {
    flex: 1,
    marginRight: 12,
    fontWeight: 'bold',
  },
  recommendedChip: {
    backgroundColor: theme => theme.colors.primary,
  },
  contentBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  personalizationScore: {
    marginBottom: 16,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scoreBar: {
    flex: 1,
    height: 8,
    borderRadius: 4,
  },
  scoreValue: {
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  reasoningSection: {
    marginBottom: 16,
  },
  reasoningTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  reasoningItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  reasoningText: {
    marginLeft: 8,
    opacity: 0.8,
    flex: 1,
  },
  contentTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagChip: {
    marginBottom: 4,
  },
  patternCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  patternHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  patternInfo: {
    flex: 1,
    marginRight: 12,
  },
  patternTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  patternFrequency: {
    opacity: 0.7,
  },
  patternMetrics: {
    alignItems: 'flex-end',
  },
  patternRecommendation: {
    marginBottom: 8,
    opacity: 0.8,
    lineHeight: 20,
  },
  patternLastObserved: {
    opacity: 0.6,
    fontSize: 12,
  },
  dialog: {
    maxHeight: '90%',
  },
  dialogContent: {
    maxHeight: 500,
  },
  dialogDescription: {
    marginBottom: 16,
    opacity: 0.8,
    lineHeight: 20,
  },
  contentDetailCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  contentDetailHeader: {
    marginBottom: 16,
  },
  contentDetailTitle: {
    fontWeight: 'bold',
  },
  contentDetailMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  detailMetric: {
    alignItems: 'center',
    flex: 1,
  },
  detailValue: {
    marginTop: 4,
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  editPreferenceCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  editPreferenceHeader: {
    marginBottom: 12,
  },
  editPreferenceTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  editPreferenceCurrent: {
    opacity: 0.7,
  },
  updatePreferenceButton: {
    marginTop: 8,
  },
  insightsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  insightsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  insightsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  insightsDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  insightCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  insightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  insightTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  insightDescription: {
    marginBottom: 12,
    opacity: 0.8,
    lineHeight: 20,
  },
  insightAction: {
    opacity: 0.8,
    lineHeight: 20,
  },
  actionLabel: {
    fontWeight: 'bold',
  },
});
